import React, { useEffect } from 'react';
import { PageSEO } from './PageSEO';

interface LocationPageSEOProps {
  city: string;
  province: string;
  citySlug: string;
  description?: string;
  keywords?: string[];
}

export const LocationPageSEO: React.FC<LocationPageSEOProps> = ({ 
  city, 
  province, 
  citySlug,
  description,
  keywords = []
}) => {
  const title = `Financial Advisor ${city}, ${province} | Wealth Management & Insurance`;
  const metaDescription = description || `Find the best financial advisor in ${city}, ${province}. Expert wealth management, insurance solutions, tax planning, and retirement strategies. Free consultation with PCM Financial.`;
  
  const defaultKeywords = [
    `financial advisor ${city}`,
    `${city} wealth management`,
    `financial planner ${city}`,
    `insurance advisor ${city}`,
    `${city} ${province} financial advisor`,
    `investment advisor ${city}`,
    `retirement planning ${city}`,
    `tax planning ${city}`,
    `PCM Financial ${city}`,
    'financial advisor near me'
  ];
  
  const allKeywords = [...defaultKeywords, ...keywords];
  
  // Structured data
  useEffect(() => {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "FinancialService",
      "name": `PCM Financial - ${city}`,
      "description": metaDescription,
      "url": `https://pcmfinancial.ca/financial-advisor-${citySlug}`,
      "telephone": "1-800-PCM-PLAN",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": city,
        "addressRegion": province,
        "addressCountry": "CA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "45.5017", // These would be dynamic in production
        "longitude": "-73.5673"
      },
      "areaServed": {
        "@type": "City",
        "name": city,
        "containedInPlace": {
          "@type": "State",
          "name": province,
          "containedInPlace": {
            "@type": "Country",
            "name": "Canada"
          }
        }
      },
      "priceRange": "$$$$",
      "image": `https://pcmfinancial.ca/assets/cities/${citySlug}-hero.jpg`,
      "sameAs": [
        "https://www.linkedin.com/company/pcm-financial",
        "https://twitter.com/pcmfinancial"
      ]
    };
    
    const breadcrumbData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://pcmfinancial.ca"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Locations",
          "item": "https://pcmfinancial.ca/locations"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": `${city} Financial Advisor`,
          "item": `https://pcmfinancial.ca/financial-advisor-${citySlug}`
        }
      ]
    };
    
    const localBusinessData = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": `PCM Financial ${city}`,
      "description": `Professional financial advisory services in ${city}, ${province}`,
      "url": `https://pcmfinancial.ca/financial-advisor-${citySlug}`,
      "telephone": "1-800-PCM-PLAN",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": city,
        "addressRegion": province,
        "addressCountry": "CA"
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "09:00",
          "closes": "17:00"
        }
      ]
    };
    
    // Add structured data scripts
    const addScript = (data: any, id: string) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      script.text = JSON.stringify(data);
      document.head.appendChild(script);
      
      return () => {
        const existingScript = document.getElementById(id);
        if (existingScript) {
          existingScript.remove();
        }
      };
    };
    
    const cleanup1 = addScript(structuredData, `location-structured-data-${citySlug}`);
    const cleanup2 = addScript(breadcrumbData, `location-breadcrumb-data-${citySlug}`);
    const cleanup3 = addScript(localBusinessData, `location-business-data-${citySlug}`);
    
    return () => {
      cleanup1();
      cleanup2();
      cleanup3();
    };
  }, [city, province, citySlug, metaDescription]);
  
  return (
    <PageSEO
      title={title}
      description={metaDescription}
      keywords={allKeywords}
      image={`/assets/cities/${citySlug}-hero.jpg`}
      type="website"
    />
  );
};